import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PlaceSelect } from '@/app/components/PlaceSelect';
import { Input } from '@/app/components/form/Input';

interface BillingAddressFormProps {
  className?: string;
}

export const BillingAddressForm = ({ className }: BillingAddressFormProps) => {
  const [countryShortName, setCountryShortName] = useState<string>('US');
  const {
    formState: { errors },
    setValue,
    register,
    watch,
  } = useFormContext();

  const getComp = (type: string, components: google.maps.GeocoderAddressComponent[] | undefined) =>
    components?.find((c) => c.types.includes(type))?.long_name || '';

  const handlePlaceSelect = (place: google.maps.places.PlaceResult | null) => {
    const countryShortName = place?.address_components?.find((c) => c.types.includes('country'))?.short_name || '';
    setCountryShortName(countryShortName);

    const components = place?.address_components;

    const streetNumber = getComp('street_number', components);
    const route = getComp('route', components);
    const city = getComp('locality', components) || getComp('sublocality', components);
    const state = getComp('administrative_area_level_1', components);
    const postalCode = getComp('postal_code', components);
    const country = getComp('country', components);

    setValue('address_line1', [streetNumber, route].filter(Boolean).join(' ').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    setValue('city', city, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue('state', state, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue('zip', postalCode, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue('country', country, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue('country_code', countryShortName, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
  };

  const handleInputChange = (value: string) => {
    setValue('address_line1', value);
  };

  const handleZipChange = (value: string) => {
    setValue('zip', value);
  };

  const handleCitySelect = (place: google.maps.places.PlaceResult | null) => {
    const components = place?.address_components;

    const city = getComp('locality', components) || getComp('sublocality', components);

    setValue('city', city);
  };

  const handleCityChange = (city: string) => {
    setValue('city', city);
  };

  const handleCountrySelect = (place: google.maps.places.PlaceResult | null) => {
    if (!place?.address_components) return;
    const country = place.address_components?.find((c) => c.types.includes('country'));
    const countryCode = country?.short_name || '';

    const countryComp = getComp('country', place.address_components);

    if (countryComp) {
      setValue('country', countryComp);
      setValue('country_code', countryCode);
      setCountryShortName(countryCode);
    }
  };

  const handleCountryChange = (country: string) => {
    setValue('country', country);
    setValue('country_code', '');
    setCountryShortName('');
    setValue('state', '');
  };

  const handleStateSelect = (place: google.maps.places.PlaceResult | null) => {
    if (!place?.address_components) {
      setValue('state', '');
      return;
    }

    const state = getComp('administrative_area_level_1', place.address_components);
    setValue('state', state);
  };

  const handleStateChange = (state: string) => {
    setValue('state', state);
  };

  const country = watch('country') ?? '';
  const zip = watch('zip') ?? '';
  const state = watch('state') ?? '';
  const city = watch('city') ?? '';
  const address_line1 = watch('address_line1') ?? '';

  return (
    <div className={className}>
      <div className="flex items-center justify-between w-full">
      <p className="md:text-xl font-canela text-base font-light text-blue-900">Billing address</p>
      </div>
      <div className="flex flex-col mt-[14px] gap-[14px]">
        <PlaceSelect
          autocompleteOptions={{
            fields: ['address_components'],
            types: ['address'],
          }}
          value={address_line1}
          autoComplete="off"
          onPlaceSelect={handlePlaceSelect}
          onInputChange={handleInputChange}
          required
          label="Billing address 1"
          placeholder="Enter street address"
          {...register('address_line1')}
          errors={errors}
        />
        <Input
          id="street2"
          label="Billing address 2"
          placeholder="Enter street address"
          {...register('address_line2')}
          errors={errors}
        />
        <div className="flex gap-[14px] w-full">
          <PlaceSelect
            autocompleteOptions={{
              fields: ['address_components'],
              types: ['postal_code'],
            }}
            onPlaceSelect={handlePlaceSelect}
            onInputChange={handleZipChange}
            value={zip}
            required
            label="Postal code"
            placeholder="Enter postal code"
            {...register('zip')}
            errors={errors}
          />
          <PlaceSelect
            autocompleteOptions={{
              fields: ['address_components'],
              types: ['(cities)'],
              componentRestrictions: { country: countryShortName || 'us' },
            }}
            onPlaceSelect={handleCitySelect}
            onInputChange={handleCityChange}
            value={city}
            required
            label="City"
            placeholder="Enter city"
            {...register('city')}
            errors={errors}
          />
        </div>
        <div className="flex gap-[14px] w-full">
          <PlaceSelect
            autocompleteOptions={{
              fields: ['address_components'],
              types: ['country'],
            }}
            onPlaceSelect={handleCountrySelect}
            onInputChange={handleCountryChange}
            value={country}
            required
            label="Country"
            placeholder="Enter country"
            {...register('country')}
            errors={errors}
          />
          <PlaceSelect
            autocompleteOptions={{
              fields: ['address_components'],
              types: ['(regions)'],
              componentRestrictions: { country: countryShortName || 'us' },
            }}
            onPlaceSelect={handleStateSelect}
            onInputChange={handleStateChange}
            key={countryShortName}
            value={state}
            label="State"
            placeholder="Enter state"
            {...register('state')}
            errors={errors}
          />
        </div>
      </div>
    </div>
  );
};
